import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { PageHeader } from '../../components/common/PageHeader';
import { MerchandiseOrder, UserProfile } from '../../types';
import { useMerchandiseData } from '../../hooks/useMerchandiseData';
import { getMembersDB } from '../../services/db';
import { isSuperAdmin } from '../../utils/formatters';
import { Modal } from '../../components/common/Modal';
import { DuitNowQrModal } from '../../components/merchandise/DuitNowQrModal';
import { ReceiptViewerModal } from '../../components/receipts/ReceiptViewerModal';
import {
  ShoppingBag,
  History,
  Package,
  Truck,
  Clock,
  X,
  CheckCircle,
  Trash2,
  AlertTriangle,
  CreditCard,
  Filter,
  QrCode,
  Copy,
  Check,
  Eye,
  MessageSquare,
  ExternalLink,
  ShieldCheck,
  FileCheck
} from 'lucide-react';

export const PembelianPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const { orders, invoices, items, isLoading, deleteOrder, saveOrder } = useMerchandiseData();

  const [members, setMembers] = useState<UserProfile[]>([]);
  useEffect(() => {
    getMembersDB().then(setMembers).catch(() => { });
  }, []);

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeOrderForQr, setActiveOrderForQr] = useState<MerchandiseOrder | null>(null);
  const [viewingReceiptUrl, setViewingReceiptUrl] = useState<string | null>(null);
  const [copiedTrackingId, setCopiedTrackingId] = useState<string | null>(null);


  // Filter to current user's orders only
  const myOrders = useMemo(() => {
    if (!currentUser) return [];
    let filtered = orders.filter(o => o.memberId === currentUser.id);
    if (statusFilter !== 'all') {
      filtered = filtered.filter(o => o.status === statusFilter);
    }
    return filtered;
  }, [orders, currentUser, statusFilter]);

  const getStatusBadgeStyle = (status: MerchandiseOrder['status']) => {
    switch (status) {
      case 'Selesai':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Sedang Disediakan':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Dibatalkan':
        return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'Menunggu Kelulusan':
      default:
        return 'bg-amber-100 text-amber-800 border-amber-200';
    }
  };

  const getStatusIcon = (status: MerchandiseOrder['status']) => {
    switch (status) {
      case 'Selesai': return <CheckCircle className="w-3.5 h-3.5" />;
      case 'Sedang Disediakan': return <Package className="w-3.5 h-3.5 animate-pulse" />;
      case 'Dibatalkan': return <X className="w-3.5 h-3.5" />;
      default: return <Clock className="w-3.5 h-3.5" />;
    }
  };

  const getInvoicePaymentStatus = (invoiceId?: string) => {
    if (!invoiceId) return 'Tiada Rujukan';
    const match = invoices.find(inv => inv.id === invoiceId);
    return match ? match.status : 'Belum Dibayar';
  };

  const handlePaymentSubmitted = async (updatedOrder: MerchandiseOrder) => {
    await saveOrder(updatedOrder);
    setActiveOrderForQr(null);
    showToast('success', 'Bukti Bayaran Dihantar!', 'Maklumat bayaran QR anda berjaya dihantar untuk pengesahan penjual.');
  };

  const handleCopyTracking = (trackingNo: string, orderId: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(trackingNo);
      setCopiedTrackingId(orderId);
      setTimeout(() => setCopiedTrackingId(null), 2000);
      showToast('success', 'Disalin!', `No. penjejakan ${trackingNo} telah disalin.`);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    setDeletingId(orderId);
    try {
      await deleteOrder(orderId);
      showToast('success', 'Tempahan Dibatalkan', `Tempahan #${orderId.slice(-6)} berjaya dibatalkan.`);
    } catch {
      showToast('error', 'Ralat', 'Gagal membatalkan tempahan.');
    } finally {
      setDeletingId(null);
    }
  };

  // Stats summary
  const stats = useMemo(() => {
    if (!currentUser) return { total: 0, pending: 0, preparing: 0, done: 0, cancelled: 0, totalSpent: 0 };
    const mine = orders.filter(o => o.memberId === currentUser.id);
    return {
      total: mine.length,
      pending: mine.filter(o => o.status === 'Menunggu Kelulusan').length,
      preparing: mine.filter(o => o.status === 'Sedang Disediakan').length,
      done: mine.filter(o => o.status === 'Selesai').length,
      cancelled: mine.filter(o => o.status === 'Dibatalkan').length,
      totalSpent: mine.filter(o => o.status !== 'Dibatalkan').reduce((acc, o) => acc + o.totalAmount, 0)
    };
  }, [orders, currentUser]);

  if (!currentUser) {
    return (
      <div className="w-full space-y-6 min-h-[75vh]">
        <PageHeader badgeText="Pembelian" badgeIcon={ShoppingBag} title="Pembelian Saya" />
        <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center space-y-3">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto" />
          <h4 className="font-extrabold text-sm text-slate-700">Log Masuk Diperlukan</h4>
          <p className="text-xs text-slate-400 font-medium">Sila log masuk untuk melihat sejarah pembelian anda.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 min-h-[75vh]">
      {/* Page Header */}
      <PageHeader
        badgeText="Tempahan Merchandise"
        badgeIcon={ShoppingBag}
        title="Pembelian Saya"
        titleBadge={stats.total > 0 ? stats.total : undefined}
      >
        <button
          type="button"
          onClick={() => {
            window.history.pushState(null, '', '/merchandise');
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-extrabold rounded-xl transition-all shadow-xs cursor-pointer active:scale-95"
        >
          <ShoppingBag className="w-3.5 h-3.5" />
          <span>Katalog Kedai</span>
        </button>
      </PageHeader>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Menunggu', value: stats.pending, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', icon: Clock },
          { label: 'Disediakan', value: stats.preparing, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', icon: Package },
          { label: 'Selesai', value: stats.done, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle },
          { label: 'Jumlah Belanja', value: `RM ${stats.totalSpent.toFixed(2)}`, color: 'text-asmm-red', bg: 'bg-red-50 border-red-200', icon: CreditCard },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} className={`${stat.bg} border rounded-2xl p-4 space-y-1`}>
              <div className="flex items-center gap-1.5">
                <Icon className={`w-4 h-4 ${stat.color}`} />
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{stat.label}</span>
              </div>
              <div className={`text-lg font-extrabold ${stat.color}`}>{stat.value}</div>
            </div>
          );
        })}
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl p-3 shadow-xs">
        <Filter className="w-4 h-4 text-slate-400 shrink-0" />
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar flex-nowrap">
          {[
            { id: 'all', label: 'Semua' },
            { id: 'Menunggu Kelulusan', label: 'Belum Bayar' },
            { id: 'Sedang Disediakan', label: 'Sedang Diproses' },
            { id: 'Selesai', label: 'Selesai' },
            { id: 'Dibatalkan', label: 'Dibatalkan' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap shrink-0 border transition-all cursor-pointer ${statusFilter === f.id
                  ? 'bg-asmm-red text-white border-asmm-red shadow-xs'
                  : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border-slate-200'
                }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Orders List */}
      {isLoading ? (
        <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center">
          <div className="w-8 h-8 border-3 border-asmm-red border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-xs text-slate-400 font-medium mt-3">Memuatkan tempahan...</p>
        </div>
      ) : myOrders.length > 0 ? (
        <div className="space-y-4">
          {myOrders.map(order => {
            const invoiceStatus = getInvoicePaymentStatus(order.invoiceId);
            const isPaid = order.paymentStatus === 'Lulus / Dibayar' || invoiceStatus === 'Lulus / Dibayar';
            const isPendingVerification = (order.paymentStatus === 'Menunggu Pengesahan' || invoiceStatus === 'Menunggu Pengesahan') && !isPaid;
            const isUnpaid = !isPaid && !isPendingVerification;
            const canCancel = order.status === 'Menunggu Kelulusan' && !isPaid;

            return (
              <div key={order.id} className="bg-white border border-slate-200 rounded-3xl p-5 sm:p-6 shadow-xs hover:shadow-sm transition-all space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 border-b border-slate-100 pb-4">
                  {/* Item & Order Identity */}
                  <div className="space-y-1.5 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[10px] font-black bg-slate-100 text-slate-800 px-2 py-0.5 rounded-lg border border-slate-200">
                        #{order.id.slice(-6).toUpperCase()}
                      </span>
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50 border border-slate-200/80 px-2 py-0.5 rounded-md">
                        {order.itemCategory}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        Tarikh: <strong className="text-slate-600">{order.createdAt}</strong>
                      </span>
                    </div>

                    <h4 className="font-extrabold text-sm sm:text-base text-slate-900 leading-tight">
                      {order.itemName}
                    </h4>

                    <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                      <span>Kuantiti: <strong className="text-slate-800">{order.quantity}</strong></span>
                      {order.size && (
                        <>
                          <span>•</span>
                          <span>Saiz: <strong className="text-slate-800">{order.size}</strong></span>
                        </>
                      )}
                      <span>•</span>
                      <span className="inline-flex items-center gap-1 font-bold text-slate-700">
                        <Truck className="w-3.5 h-3.5 text-slate-400" />
                        {order.deliveryMethod}
                      </span>
                    </div>

                    {order.notes && (
                      <p className="text-[11px] text-slate-500 italic bg-slate-50 p-2 rounded-xl border border-slate-100">
                        Nota: {order.notes}
                      </p>
                    )}
                  </div>

                  {/* Price & Primary Action */}
                  <div className="flex sm:flex-col items-center sm:items-end justify-between gap-2 shrink-0">
                    <div className="text-right">
                      <span className="text-[10px] font-bold text-slate-400 uppercase block">Jumlah Pesanan</span>
                      <span className="text-lg sm:text-xl font-black text-asmm-red font-mono">
                        RM {order.totalAmount.toFixed(2)}
                      </span>
                    </div>

                    {/* QR Payment CTA if unpaid */}
                    {isUnpaid && order.status !== 'Dibatalkan' && (
                      <button
                        type="button"
                        onClick={() => setActiveOrderForQr(order)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-md cursor-pointer active:scale-95 animate-pulse"
                      >
                        <QrCode className="w-3.5 h-3.5" />
                        <span>Bayar Melalui QR</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Status & Payment Pipeline Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                  {/* Box 1: Status Pembayaran QR */}
                  <div className="p-3.5 bg-slate-50/80 rounded-2xl border border-slate-200/80 flex items-center justify-between gap-3 flex-wrap">
                    <div className="space-y-0.5">
                      <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">
                        Kaedah Bayaran: DuitNow QR
                      </span>
                      <div className="flex items-center gap-1.5">
                        {isPaid ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-800 border border-emerald-300">
                            <CheckCircle className="w-3 h-3" />
                            <span>Bayaran QR Disahkan (Lulus)</span>
                          </span>
                        ) : isPendingVerification ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black bg-blue-100 text-blue-800 border border-blue-300">
                            <Clock className="w-3 h-3 animate-spin" />
                            <span>Menunggu Pengesahan Penjual</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black bg-rose-100 text-rose-800 border border-rose-300">
                            <AlertTriangle className="w-3 h-3" />
                            <span>Belum Dibayar</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {order.paymentProofUrl && (
                        <button
                          type="button"
                          onClick={() => setViewingReceiptUrl(order.paymentProofUrl || null)}
                          className="px-2.5 py-1 bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 text-[10px] font-extrabold rounded-lg transition-all flex items-center gap-1 cursor-pointer"
                        >
                          <Eye className="w-3 h-3" />
                          <span>Lihat Resit</span>
                        </button>
                      )}

                      {!isPaid && order.status !== 'Dibatalkan' && (
                        <button
                          type="button"
                          onClick={() => setActiveOrderForQr(order)}
                          className="px-2.5 py-1 bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-extrabold rounded-lg transition-all flex items-center gap-1 cursor-pointer"
                        >
                          <QrCode className="w-3 h-3" />
                          <span>{isPendingVerification ? 'Kemaskini Resit' : 'Buka Kod QR'}</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Box 2: Status Penyediaan & Penghantaran */}
                  <div className="p-3.5 bg-slate-50/80 rounded-2xl border border-slate-200/80 flex items-center justify-between gap-3 flex-wrap">
                    <div className="space-y-0.5">
                      <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">
                        Status Penyediaan & Serahan
                      </span>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-extrabold border ${getStatusBadgeStyle(order.status)}`}>
                        {getStatusIcon(order.status)}
                        <span>{order.status}</span>
                      </span>
                    </div>

                    {order.deliveryMethod === 'Penghantaran Pos' ? (
                      order.trackingNo ? (
                        <div className="flex items-center gap-1.5 bg-blue-50 px-3 py-1.5 rounded-xl border border-blue-200">
                          <Truck className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                          <div>
                            <span className="text-[9px] font-bold text-blue-500 uppercase block leading-none">Tracking Pos</span>
                            <span className="font-mono text-xs font-black text-blue-900">{order.trackingNo}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCopyTracking(order.trackingNo!, order.id)}
                            className="p-1.5 hover:bg-blue-100 text-blue-700 rounded-lg cursor-pointer transition-all ml-1"
                            title="Salin No Tracking"
                          >
                            {copiedTrackingId === order.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-[10px] font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200">
                          <Truck className="w-3 h-3 text-amber-500" />
                          <span>No. tracking akan dikemaskini oleh penjual</span>
                        </div>
                      )
                    ) : (
                      <div className="text-[10px] font-bold text-slate-500 flex items-center gap-1">
                        <span>📍 Ambil di {order.pickupLocation || order.gelanggang || 'Gelanggang'}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer Controls: WhatsApp Penjual & Batal Tempahan */}
                <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100 flex-wrap">
                  {(() => {
                    const item = items.find(i => i.id === order.itemId);
                    const sellerId = item?.userId || 'asmm-001';
                    const seller = members.find(m => m.id === sellerId);
                    const sellerPhone = seller?.phone || '0194727404';
                    let cleanSellerPhone = sellerPhone.replace(/[^0-9]/g, '');
                    if (cleanSellerPhone.startsWith('0')) cleanSellerPhone = '6' + cleanSellerPhone;
                    else if (!cleanSellerPhone.startsWith('60') && cleanSellerPhone.startsWith('1')) cleanSellerPhone = '60' + cleanSellerPhone;

                    return (
                      <a
                        href={`https://wa.me/${cleanSellerPhone}?text=${encodeURIComponent(
                          `Salam penjual ASMM, saya (${currentUser.name}) ingin bertanya status tempahan *#${order.id.slice(-6).toUpperCase()}* bagi produk *${order.itemName}* (RM${order.totalAmount.toFixed(2)}).`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-xs font-extrabold transition-all no-underline"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        <span>Tanya Penjual (WhatsApp)</span>
                      </a>
                    );
                  })()}

                  {canCancel && (
                    <button
                      type="button"
                      onClick={() => handleCancelOrder(order.id)}
                      disabled={deletingId === order.id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-rose-600 hover:text-white hover:bg-rose-600 bg-rose-50 border border-rose-200 rounded-xl transition-all cursor-pointer disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>{deletingId === order.id ? 'Membatalkan...' : 'Batal Tempahan'}</span>
                    </button>
                  )}

                  {isSuperAdmin(currentUser) && order.status === 'Dibatalkan' && (
                    <button
                      type="button"
                      onClick={() => handleCancelOrder(order.id)}
                      disabled={deletingId === order.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-extrabold text-white bg-rose-600 hover:bg-rose-700 rounded-xl transition-all shadow-xs cursor-pointer disabled:opacity-50 active:scale-95"
                      title="Padam rekod tempahan batal secara kekal (Superadmin)"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>{deletingId === order.id ? 'Memadam...' : 'Padam Rekod Batal'}</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center space-y-3">
          <History className="w-12 h-12 text-slate-300 mx-auto" />
          <h4 className="font-extrabold text-sm text-slate-700">Tiada Tempahan Dijumpai</h4>
          <p className="text-xs text-slate-400 font-medium max-w-md mx-auto">
            {statusFilter !== 'all'
              ? 'Tiada tempahan dengan status yang dipilih. Cuba ubah penapis.'
              : 'Anda belum membuat sebarang tempahan. Kunjungi halaman Kedai ASMM untuk menempah barangan rasmi persatuan.'}
          </p>
          <button
            type="button"
            onClick={() => {
              window.history.pushState(null, '', '/merchandise');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-extrabold rounded-xl transition-all shadow-xs cursor-pointer active:scale-95 mt-2"
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            <span>Katalog Kedai Merchandise</span>
          </button>
        </div>
      )}

      {/* DuitNow QR Payment Modal */}
      <DuitNowQrModal
        isOpen={Boolean(activeOrderForQr)}
        onClose={() => setActiveOrderForQr(null)}
        order={activeOrderForQr}
        onPaymentSubmitted={handlePaymentSubmitted}
      />

      {/* Full Receipt Image Preview Modal */}
      <ReceiptViewerModal
        isOpen={Boolean(viewingReceiptUrl)}
        onClose={() => setViewingReceiptUrl(null)}
        receiptUrl={viewingReceiptUrl}
        title="Bukti Resit Pembayaran Pesanan"
      />
    </div>
  );
};
